import React, { useState } from 'react';
import Grid from '@mui/material/Grid';
import OutlinedInput from '@mui/material/OutlinedInput';
import { Select, MenuItem, Paper, Button, Box, Collapse, IconButton, Typography, Drawer, Badge, Divider } from '@mui/material';
import InputLabel from '@mui/material/InputLabel';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import SpeedDial from '@mui/material/SpeedDial';
import SpeedDialIcon from '@mui/material/SpeedDialIcon';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CloseIcon from '@mui/icons-material/Close';
import DefaultSelectClient from '../../../components/default-select-client';
import ContractDataTable, { IContractDataTableProps } from './components/contract-data-table';
import { useContractSearchController } from './context/contract-search-controller';
import { useContractSearchState } from './context/contract-search-context';
import PageHeader from '../../../components/page-header';
import { ContractSortField } from '../../../model/scp/contract/contract-sort-field';
import { SortDirection } from '../../../model/scp/base/sort-direction';

const CONTRACT_SORT_OPTIONS = [
    { field: ContractSortField.UpdatedDate, label: 'Data de Alteração' },
    { field: ContractSortField.CreatedDate, label: 'Data de Criação' },
    { field: ContractSortField.ContractEffortNumber, label: 'Nº Empenho' },
    { field: ContractSortField.NumberProcess, label: 'Nº Processo' },
    { field: ContractSortField.Year, label: 'Exercício' },
    { field: ContractSortField.SupplierName, label: 'Fornecedor' },
    { field: ContractSortField.ContractType, label: 'Tipo de Contrato' },
    { field: ContractSortField.ProcessCategory, label: 'Categoria de Processo' },
].flatMap((option) => ([
    { value: `${option.field}_${SortDirection.Ascending}`, label: `${option.label} Ascendente` },
    { value: `${option.field}_${SortDirection.Descending}`, label: `${option.label} Descendente` },
]));

export function ContractSearchComponent() {
    const [filtersOpen, setFiltersOpen] = useState(true);
    const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);

    const {
        handleOnChange,
        handleSearch,
        handleClearFilters,
        navigateToCreate
    } = useContractSearchController();

    const {
        typeContract,
        categoryProcess,
        parameters,
        searchTrigger
    } = useContractSearchState();

    const dataTableProperties: IContractDataTableProps = {
        parameters: parameters,
        searchTrigger: searchTrigger
    };

    const activeExtraFiltersCount = [
        parameters.contractTypeId,
        parameters.categoryProcessId,
        parameters.year,
        parameters.clientId
    ].filter(Boolean).length;

    const handleApplyMoreFilters = () => {
        setMoreFiltersOpen(false);
        handleSearch();
    };

    return (
        <React.Fragment>
            <Grid item xs={12} md={12} lg={12}>
                <PageHeader
                    title="Consulta de Contratos"
                    subtitle="Pesquise e gerencie todos os contratos cadastrados no sistema"
                    breadcrumbs={[
                        { label: 'Contratos' }
                    ]}
                />
            </Grid>

            <Grid item xs={12} md={12} lg={12}>
                <Paper sx={{ p: 2 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: filtersOpen ? 2 : 0,
                            cursor: 'pointer',
                            '&:hover': {
                                backgroundColor: 'action.hover'
                            },
                            borderRadius: 1,
                            p: 1,
                            ml: -1,
                            mr: -1
                        }}
                        onClick={() => setFiltersOpen(!filtersOpen)}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FilterListIcon color="action" />
                            <Typography variant="h6" component="h2">
                                Filtros
                            </Typography>
                        </Box>
                        <IconButton
                            size="small"
                            sx={{ pointerEvents: 'none' }}
                        >
                            {filtersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </Box>

                    <Collapse in={filtersOpen}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                                <Box sx={{ flex: '1 1 260px' }}>
                                    <InputLabel>Buscar por</InputLabel>
                                    <OutlinedInput
                                        name="input-term"
                                        fullWidth
                                        value={parameters.term || ''}
                                        onChange={handleOnChange}
                                    />
                                </Box>
                                <Box sx={{ flex: '0 1 240px', minWidth: 200 }}>
                                    <InputLabel>Ordenar por</InputLabel>
                                    <Select
                                        fullWidth
                                        name="select-sort"
                                        value={parameters.sortBy !== undefined && parameters.sortDirection !== undefined
                                            ? `${parameters.sortBy}_${parameters.sortDirection}`
                                            : ''}
                                        onChange={handleOnChange}
                                    >
                                        <MenuItem value="">
                                            <em>Padrão</em>
                                        </MenuItem>
                                        {CONTRACT_SORT_OPTIONS.map((option) => (
                                            <MenuItem value={option.value} key={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    <Badge badgeContent={activeExtraFiltersCount} color="primary" invisible={activeExtraFiltersCount === 0}>
                                        <Button
                                            variant="outlined"
                                            startIcon={<FilterListIcon />}
                                            onClick={() => setMoreFiltersOpen(true)}
                                        >
                                            Mais Filtros
                                        </Button>
                                    </Badge>
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        startIcon={<ClearIcon />}
                                        onClick={handleClearFilters}
                                    >
                                        Limpar
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<SearchIcon />}
                                        onClick={handleSearch}
                                    >
                                        Consultar
                                    </Button>
                                </Box>
                            </Box>
                        </LocalizationProvider>
                    </Collapse>
                </Paper>
            </Grid>

            <Drawer
                anchor="right"
                open={moreFiltersOpen}
                onClose={() => setMoreFiltersOpen(false)}
                sx={{ zIndex: (theme) => theme.zIndex.drawer + 2 }}
            >
                <Box sx={{ width: 340, p: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FilterListIcon color="action" />
                            <Typography variant="h6">Mais Filtros</Typography>
                        </Box>
                        <IconButton size="small" onClick={() => setMoreFiltersOpen(false)}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    <Divider />

                    <Box>
                        <InputLabel>Tipo de Contrato</InputLabel>
                        <Select
                            fullWidth
                            name="select-contract-type"
                            value={parameters.contractTypeId || ''}
                            onChange={handleOnChange}
                        >
                            <MenuItem value="">
                                <em>Selecionar</em>
                            </MenuItem>
                            {typeContract.map((element) => (
                                <MenuItem value={element.id.toString()} key={element.id}>
                                    {element.id} - {element.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>

                    <Box>
                        <InputLabel>Categoria do Processo</InputLabel>
                        <Select
                            fullWidth
                            name="select-category-process"
                            value={parameters.categoryProcessId || ''}
                            onChange={handleOnChange}
                        >
                            <MenuItem value="">
                                <em>Selecionar</em>
                            </MenuItem>
                            {categoryProcess.map((element) => (
                                <MenuItem value={element.id.toString()} key={element.id}>
                                    {element.id} - {element.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>

                    <Box>
                        <InputLabel>Exercício</InputLabel>
                        <OutlinedInput
                            name="input-year"
                            fullWidth
                            type="number"
                            value={parameters.year || ''}
                            onChange={handleOnChange}
                            inputProps={{ min: 2000, max: new Date().getFullYear() + 1 }}
                        />
                    </Box>

                    <Box>
                        <DefaultSelectClient
                            required={false}
                            clientId={parameters.clientId}
                            handleOnChange={handleOnChange}
                        />
                    </Box>

                    <Box sx={{ mt: 'auto', display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button
                            variant="outlined"
                            color="secondary"
                            startIcon={<ClearIcon />}
                            onClick={handleClearFilters}
                        >
                            Limpar
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<SearchIcon />}
                            onClick={handleApplyMoreFilters}
                        >
                            Aplicar
                        </Button>
                    </Box>
                </Box>
            </Drawer>

            <Grid item xs={12} md={12} lg={12}>
                <Paper
                    sx={{
                        p: 2,
                        height: "500px",
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <ContractDataTable props={dataTableProperties} />
                    <SpeedDial
                        ariaLabel="SpeedDial basic example"
                        sx={{ position: 'absolute', bottom: 16, right: 16 }}
                        onClick={navigateToCreate}
                        icon={<SpeedDialIcon />}
                    />
                </Paper>
            </Grid>
        </React.Fragment>
    );
}
